import React, { useState } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { Input, ListItemIcon, ListItemText, NavGroupL2, NavGroupTitleL2, NavItemL2 } from '@mesh.ai/rabbit';
import { meshModules } from 'Deer/constants/modules';
import { staticRoutes } from 'Deer/constants/routes';
import { useRouteToMyTeamLevels } from 'Deer/hooks/useRouteToL3';
import Fuse from 'fuse.js';
import { NavLink } from 'react-router-dom';

import { sendProfilePageViewEvent } from 'constants/mixpanelHelper';

import { usePeople } from './hooks/usePeople';

const activeClassName = 'active-L2';

const PeopleItem = ({
  profilePhoto,
  displayName,
  email,
  href,
}: {
  profilePhoto: string;
  displayName: string;
  email: string;
  href: string;
}) => {
  const { teamType } = useRouteToMyTeamLevels();

  return (
    <NavLink
      key={email}
      to={href}
      activeClassName={activeClassName}
      onClick={() => {
        sendProfilePageViewEvent({
          eventProperties: { source: teamType === 'directReports' ? 'Direct Team' : 'Skip Team' },
        });
      }}
    >
      <NavItemL2
        style={{
          width: '100%',
          marginBottom: '4px',
        }}
      >
        <ListItemIcon shape="round" icon={profilePhoto} />
        <Box
          sx={{
            span: {
              maxWidth: '100%',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            },
          }}
          w="80%"
        >
          <ListItemText title={displayName} subTitle={email} />
        </Box>
      </NavItemL2>
    </NavLink>
  );
};

const PeopleList = () => {
  const { teamType } = useRouteToMyTeamLevels();

  const viewId = teamType === 'allReports' ? -3 : -2;

  const { data: people = [], isLoading } = usePeople({
    cohortId: viewId,
    module: meshModules.goals.value,
  });

  // fuzzy search
  const [searchTerm, setSearchTerm] = useState('');

  const fuse = new Fuse(people, {
    keys: ['email', 'displayName'],
    threshold: 0.4,
  });

  const results = fuse.search(searchTerm);

  const handleTextChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const showSearch = people.length > 10;

  if (teamType === 'view') {
    return null;
  }

  if (isLoading) {
    return null;
  }

  return (
    <NavGroupL2>
      <NavGroupTitleL2>
        {/* @ts-expect-error string | element */}
        <Flex alignItems="center" width="100%" justifyContent="space-between">
          <span>PEOPLE</span>
        </Flex>
      </NavGroupTitleL2>

      {showSearch && (
        <Input
          placeholder="Search People"
          onChange={handleTextChange}
          value={searchTerm}
          style={{
            minWidth: 0,
          }}
          type="search"
        />
      )}

      <Box overflow="auto" height={`calc(100vh - ${340}px)`}>
        {searchTerm.length
          ? results?.map(({ item: { profilePhoto, displayName, email, id } }) => {
              const href = staticRoutes.team[teamType].profile.replace(':employeeId', String(id));
              return (
                <PeopleItem
                  key={email}
                  profilePhoto={profilePhoto}
                  displayName={displayName}
                  email={email}
                  href={href}
                />
              );
            })
          : null}

        {searchTerm.length === 0
          ? people?.map(({ profilePhoto, displayName, email, id }) => {
              const href = staticRoutes.team[teamType].profile.replace(':employeeId', String(id));

              return (
                <PeopleItem
                  key={email}
                  profilePhoto={profilePhoto}
                  displayName={displayName}
                  email={email}
                  href={href}
                />
              );
            })
          : null}
      </Box>
    </NavGroupL2>
  );
};

export { PeopleList };
